/**
 * Layers v2 (Photoshop-like): cada página renderiza uma pilha de layers.
 *
 * Text:  { id, name, type: 'text', role: 'title'|'content'|'subtitle'|null,
 *           html, style: {}, box: { x, y, w }, visible, locked }
 * Image: { id, name, type: 'image', src, mode, opacity, scale, x, y,
 *           visible, locked }
 *
 * - `box` em % do slide (h automática). Roles só existem para migrar o
 *   legado (título/conteúdo/legenda); slides novos usam 1 box livre.
 * - `visible:false` esconde sem deletar; `locked` trava no canvas.
 */

import { getVideoId, getYouTubeUrl, isYoutubeUrl } from './youtube';

export function newId(prefix = 'l') {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
  } catch {
    // fallback abaixo
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export const ROLE_BOXES = {
  header: { x: 5, y: 4, w: 90 },
  title: { x: 5, y: 4, w: 90 },
  content: { x: 5, y: 20, w: 90 },
  footer: { x: 5, y: 80, w: 90 },
  subtitle: { x: 5, y: 80, w: 90 },
};

export const FREE_BOX = { x: 5, y: 20, w: 90 };

/**
 * Garante box de posicionamento (mutando o original) e devolve sanitizado.
 * Mesma função no stage, rail e output: sem ela, box ausente vira estilo
 * inválido e a layer " Cai" para full-bleed/static.
 */
export function layerBox(layer) {
  if (!layer || typeof layer !== 'object') return { ...FREE_BOX };
  const b = (layer.box && typeof layer.box === 'object') ? layer.box : null;
  if (b) {
    return {
      x: Number.isFinite(Number(b.x)) ? Number(b.x) : 0,
      y: Number.isFinite(Number(b.y)) ? Number(b.y) : 0,
      w: Number(b.w) > 0 ? Number(b.w) : 50,
    };
  }
  let fresh = null;
  if (layer.type === 'image') {
    if (layer.mode === 'custom' && layer.scale) {
      fresh = { x: layer.x ?? 0, y: layer.y ?? 0, w: layer.scale };
    } else {
      fresh = { x: 0, y: 0, w: 100 };
    }
  } else {
    fresh = { ...(ROLE_BOXES[layer.role] || FREE_BOX) };
  }
  layer.box = fresh;
  return { ...fresh };
}

export function createTextBox({ html = '', name = 'Texto', role = null, style = {}, box = null, opacity = 1, blur = 0, borderRadius = 0, group = 'primary', visible = true, locked = false } = {}) {
  let defaultStyle = { fontSize: '64px', color: '#FFFFFF', textAlign: 'center' };
  if (role === 'header' || role === 'title') {
    defaultStyle = { fontSize: '32px', color: '#60A5FA', textAlign: 'center' };
  } else if (role === 'footer' || role === 'subtitle') {
    defaultStyle = { fontSize: '32px', color: '#FDE68A', textAlign: 'center' };
  }
  return {
    id: newId('l'),
    name,
    type: 'text',
    role,
    html: html ?? '',
    style: { ...defaultStyle, ...(style || {}) },
    box: { ...(ROLE_BOXES[role] || FREE_BOX), ...(box || {}) },
    opacity: Number.isFinite(Number(opacity)) ? Number(opacity) : 1,
    blur: Number.isFinite(Number(blur)) ? Number(blur) : 0,
    borderRadius: Number.isFinite(Number(borderRadius)) ? Number(borderRadius) : 0,
    group: group === 'secondary' ? 'secondary' : 'primary',
    visible,
    locked,
  };
}

export function createImageLayer({ src = '', name = 'Imagem', mode = 'cover', opacity = 1, blur = 0, borderRadius = 0, scale = 100, x = 0, y = 0, box = null, group = 'primary', visible = true, locked = false } = {}) {
  return {
    id: newId('l'),
    name,
    type: 'image',
    src,
    mode,
    opacity: Number.isFinite(Number(opacity)) ? Number(opacity) : 1,
    blur: Number.isFinite(Number(blur)) ? Number(blur) : 0,
    borderRadius: Number.isFinite(Number(borderRadius)) ? Number(borderRadius) : 0,
    scale,
    x,
    y,
    box: box && typeof box === 'object' ? { ...box } : null,
    group: group === 'secondary' ? 'secondary' : 'primary',
    visible,
    locked,
  };
}

export function createVideoLayer({
  videoId = '',
  url = '',
  loop = false,
  start = 0,
  end = null,
  opacity = 1,
  blur = 0,
  borderRadius = 0,
  name = 'Vídeo',
  box = null,
  group = 'primary',
  visible = true,
  locked = false
} = {}) {
  const vid = getVideoId(videoId) || getVideoId(url) || (videoId?.length === 11 ? videoId : '') || (url?.length === 11 ? url : '') || '';
  const cleanUrl = vid ? getYouTubeUrl(vid) : (url || '');
  return {
    id: newId('l'),
    name,
    type: 'video',
    videoId: vid,
    url: cleanUrl,
    loop: !!loop,
    start: Number.isFinite(Number(start)) ? Number(start) : 0,
    end: end !== null && end !== undefined && end !== '' && Number.isFinite(Number(end)) ? Number(end) : null,
    opacity: Number.isFinite(Number(opacity)) ? Number(opacity) : 1,
    blur: Number.isFinite(Number(blur)) ? Number(blur) : 0,
    borderRadius: Number.isFinite(Number(borderRadius)) ? Number(borderRadius) : 0,
    box: box && typeof box === 'object' ? { ...box } : { x: 5, y: 5, w: 90 },
    group: group === 'secondary' ? 'secondary' : 'primary',
    visible,
    locked,
  };
}

/** Um slide novo: 3 caixas vazias (cabeçalho, conteúdo, rodapé). Nomes via i18n no chamador. */
export function newSlideLayers(names = {}) {
  return [
    createTextBox({ html: '', name: names.header || 'Header', role: 'header', box: { x: 5, y: 4, w: 90 }, style: { fontSize: '32px', color: '#60A5FA', textAlign: 'center' } }),
    createTextBox({ html: '', name: names.content || 'Content', role: 'content', box: { x: 5, y: 24, w: 90 }, style: { fontSize: '64px', color: '#FFFFFF', textAlign: 'center' } }),
    createTextBox({ html: '', name: names.footer || 'Footer', role: 'footer', box: { x: 5, y: 80, w: 90 }, style: { fontSize: '32px', color: '#FDE68A', textAlign: 'center' } }),
  ];
}

function isNonEmptyHtml(html) {
  if (!html) return false;
  const text = String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return text.length > 0;
}

export { isNonEmptyHtml };

/**
 * Garante layers em página interna (v1-flat). Blank fica sem layers;
 * páginas ganham caixas correspondentes aos campos (header, content, footer).
 * Idempotente: nunca duplica.
 */
export function ensurePageLayers(page) {
  if (!page || typeof page !== 'object') return [];
  if (Array.isArray(page.layers) && page.layers.length > 0) return page.layers;
  if (page.editable === false) {
    page.layers = [];
    return page.layers;
  }
  const layers = [];
  if (page.background) {
    layers.push(createImageLayer({ name: 'Fundo', src: page.background }));
  }
  const hasHeader = isNonEmptyHtml(page.header) || isNonEmptyHtml(page.title);
  const hasContent = isNonEmptyHtml(page.content);
  const hasFooter = isNonEmptyHtml(page.footer) || isNonEmptyHtml(page.subtitle);

  if (hasHeader || hasContent || hasFooter) {
    if (hasHeader) {
      layers.push(createTextBox({
        name: 'Header',
        role: 'header',
        html: page.header || page.title || '',
        box: { x: 5, y: 4, w: 90 },
        style: { fontSize: '32px', color: '#60A5FA', textAlign: 'center' },
      }));
    }
    if (hasContent) {
      const ps = (page.style && typeof page.style === 'object') ? page.style : {};
      const boxY = (hasHeader || hasFooter) ? 24 : 18;
      layers.push(createTextBox({
        name: 'Conteúdo',
        role: 'content',
        html: page.content || '',
        box: { x: 5, y: boxY, w: 90 },
        style: { fontSize: '64px', color: '#FFFFFF', textAlign: 'center', ...ps },
      }));
    }
    if (hasFooter) {
      layers.push(createTextBox({
        name: 'Footer',
        role: 'footer',
        html: page.footer || page.subtitle || '',
        box: { x: 5, y: 80, w: 90 },
        style: { fontSize: '32px', color: '#FDE68A', textAlign: 'center' },
      }));
    }
  } else {
    layers.push(...newSlideLayers());
  }
  page.layers = layers;
  return layers;
}

/**
 * Sintetiza layers a partir de página legada (campos planos).
 * Ordem: fundo, header, conteúdo, footer.
 */
export function synthesizeLayersFromLegacy(page) {
  const p = page && typeof page === 'object' ? page : {};
  const layers = [];
  if (p.background) {
    if (p.backgroundType === 'video' || isYoutubeUrl(p.background)) {
      const vid = getVideoId(p.videoId) || getVideoId(p.background) || '';
      layers.push(createVideoLayer({
        name: 'Vídeo de Fundo',
        videoId: vid,
        url: vid ? getYouTubeUrl(vid) : p.background,
        loop: true,
      }));
    } else {
      layers.push(createImageLayer({
        name: 'Fundo',
        src: p.background,
        ...(p.backgroundAdjust && typeof p.backgroundAdjust === 'object' ? p.backgroundAdjust : {}),
      }));
    }
  } else if (p.videoId || p.backgroundType === 'video') {
    const vid = getVideoId(p.videoId) || '';
    if (vid) {
      layers.push(createVideoLayer({
        name: 'Vídeo de Fundo',
        videoId: vid,
        url: getYouTubeUrl(vid),
        loop: true,
      }));
    }
  }
  const baseStyle = (p.style && typeof p.style === 'object') ? { ...p.style } : {};
  const hasHeader = isNonEmptyHtml(p.header) || isNonEmptyHtml(p.title);
  const hasFooter = isNonEmptyHtml(p.footer) || isNonEmptyHtml(p.subtitle);
  const contentY = (hasHeader || hasFooter) ? 24 : 18;
  const roles = [
    ['header', p.header || p.title, 'Header', { fontSize: '32px', color: '#60A5FA', textAlign: 'center' }, { x: 5, y: 4, w: 90 }],
    ['content', p.content, 'Conteúdo', { fontSize: '64px', color: '#FFFFFF', textAlign: 'center', ...baseStyle }, { x: 5, y: contentY, w: 90 }],
    ['footer', p.footer || p.subtitle, 'Footer', { fontSize: '32px', color: '#FDE68A', textAlign: 'center' }, { x: 5, y: 80, w: 90 }],
  ];
  roles.forEach(([role, html, name, defaultStyle, defaultBox]) => {
    if (!isNonEmptyHtml(html)) return;
    layers.push(createTextBox({
      html,
      name,
      role,
      style: { ...defaultStyle },
      box: { ...(defaultBox || ROLE_BOXES[role] || FREE_BOX) },
    }));
  });
  return layers;
}

/** Normaliza layers vindas do doc (garante ids/box/visibilidade/grupos). */
export function normalizeLayers(layers) {
  if (!Array.isArray(layers)) return [];
  return layers
    .filter((l) => l && typeof l === 'object')
    .map((l) => {
      const group = l.group === 'secondary' ? 'secondary' : 'primary';
      if (l.type === 'image') {
        let box = null;
        if (l.box && typeof l.box === 'object') {
          box = { x: 0, y: 0, w: 100, ...l.box };
        } else if (l.mode === 'custom' && l.scale) {
          box = { x: l.x ?? 0, y: l.y ?? 0, w: l.scale };
        } else {
          box = { x: 0, y: 0, w: 100 };
        }
        return {
          id: l.id || newId('l'),
          name: l.name || 'Imagem',
          type: 'image',
          src: l.src || '',
          mode: l.mode || 'cover',
          opacity: Number.isFinite(Number(l.opacity)) ? Number(l.opacity) : 1,
          blur: Number.isFinite(Number(l.blur)) ? Number(l.blur) : 0,
          borderRadius: Number.isFinite(Number(l.borderRadius)) ? Number(l.borderRadius) : 0,
          scale: Number(l.scale) > 0 ? Number(l.scale) : 100,
          x: Number.isFinite(Number(l.x)) ? Number(l.x) : 0,
          y: Number.isFinite(Number(l.y)) ? Number(l.y) : 0,
          box,
          group,
          visible: l.visible !== false,
          locked: !!l.locked,
        };
      }
      if (l.type === 'video') {
        const vid = getVideoId(l.videoId) || getVideoId(l.url) || (l.videoId?.length === 11 ? l.videoId : '') || (l.url?.length === 11 ? l.url : '') || '';
        const cleanUrl = vid ? getYouTubeUrl(vid) : (l.url || '');
        return {
          id: l.id || newId('l'),
          name: l.name || 'Vídeo',
          type: 'video',
          videoId: vid,
          url: cleanUrl,
          loop: !!l.loop,
          start: Number.isFinite(Number(l.start)) ? Number(l.start) : 0,
          end: l.end !== null && l.end !== undefined && l.end !== '' && Number.isFinite(Number(l.end)) ? Number(l.end) : null,
          opacity: Number.isFinite(Number(l.opacity)) ? Number(l.opacity) : 1,
          blur: Number.isFinite(Number(l.blur)) ? Number(l.blur) : 0,
          borderRadius: Number.isFinite(Number(l.borderRadius)) ? Number(l.borderRadius) : 0,
          box: (l.box && typeof l.box === 'object')
            ? { x: 0, y: 0, w: 100, ...l.box }
            : { x: 5, y: 5, w: 90 },
          group,
          visible: l.visible !== false,
          locked: !!l.locked,
        };
      }
      let defaultStyle = { fontSize: '64px', color: '#FFFFFF', textAlign: 'center' };
      if (l.role === 'header' || l.role === 'title') {
        defaultStyle = { fontSize: '32px', color: '#60A5FA', textAlign: 'center' };
      } else if (l.role === 'footer' || l.role === 'subtitle') {
        defaultStyle = { fontSize: '32px', color: '#FDE68A', textAlign: 'center' };
      }
      return {
        id: l.id || newId('l'),
        name: l.name || (l.role === 'header' || l.role === 'title' ? 'Header' : l.role === 'footer' || l.role === 'subtitle' ? 'Footer' : 'Conteúdo'),
        type: 'text',
        role: l.role ?? null,
        html: l.html ?? l.content ?? '',
        style: { ...defaultStyle, ...((l.style && typeof l.style === 'object') ? l.style : {}) },
        box: { ...(ROLE_BOXES[l.role] || FREE_BOX), ...((l.box && typeof l.box === 'object') ? l.box : {}) },
        opacity: Number.isFinite(Number(l.opacity)) ? Number(l.opacity) : 1,
        blur: Number.isFinite(Number(l.blur)) ? Number(l.blur) : 0,
        borderRadius: Number.isFinite(Number(l.borderRadius)) ? Number(l.borderRadius) : 0,
        group,
        visible: l.visible !== false,
        locked: !!l.locked,
      };
    });
}

/**
 * Achata layers de volta para campos planos (leitores legados / protocolo).
 * Primeira imagem visível vira background; roles viram title/header/content/subtitle/footer;
 * boxes livres sem role caem no content.
 */
export function flattenLayers(layers) {
  const list = normalizeLayers(layers);
  const byRole = (role) => list.find((l) => l.type === 'text' && (l.role === role || (role === 'header' && l.role === 'title') || (role === 'footer' && l.role === 'subtitle')) && l.visible !== false);
  const free = list.filter((l) => l.type === 'text' && !l.role && l.visible !== false);
  const header = byRole('header') || byRole('title');
  const content = list.find((l) => l.type === 'text' && l.role === 'content' && l.visible !== false);
  const footer = byRole('footer') || byRole('subtitle');
  const img = list.find((l) => l.type === 'image' && l.visible !== false && l.src);
  const contentHtml = [
    ...(content ? [content.html] : []),
    ...free.map((l) => l.html),
  ].filter((h) => isNonEmptyHtml(h)).join('<br><br>');
  return {
    title: header ? header.html : '',
    header: header ? header.html : '',
    content: contentHtml,
    subtitle: footer ? footer.html : '',
    footer: footer ? footer.html : '',
    background: img ? img.src : '',
    style: { ...((content || free[0] || {}).style || {}) },
  };
}

export function layerStyle(layer) {
  if (!layer) return {};
  const opacity = Number.isFinite(Number(layer.opacity)) ? Number(layer.opacity) : 1;
  const blur = Number.isFinite(Number(layer.blur)) ? Number(layer.blur) : 0;
  const borderRadius = Number.isFinite(Number(layer.borderRadius)) ? Number(layer.borderRadius) : 0;
  const style = {};
  if (opacity !== 1) style.opacity = opacity;
  if (blur > 0) style.filter = `blur(${blur}px)`;
  if (borderRadius > 0) style.borderRadius = `${borderRadius}px`;
  return style;
}

export function imageLayerStyle(layer) {
  return layerStyle(layer);
}

export function videoEmbedUrl(layer, options = {}) {
  if (!layer) return '';
  const vid = getVideoId(layer.videoId) || getVideoId(layer.url) || (layer.videoId?.length === 11 ? layer.videoId : '') || '';
  if (!vid) return '';
  const start = Number(layer.start) || 0;
  const end = layer.end ? Number(layer.end) : null;
  const loop = !!layer.loop;
  const autoplay = options.autoplay !== undefined ? (options.autoplay ? 1 : 0) : 0;
  const controls = options.controls !== undefined ? (options.controls ? 1 : 0) : 0;
  const mute = options.mute !== undefined ? (options.mute ? 1 : 0) : 0;

  const params = new URLSearchParams();
  params.set('enablejsapi', '1');
  params.set('autoplay', String(autoplay));
  params.set('controls', String(controls));
  if (mute) params.set('mute', '1');
  if (start > 0) params.set('start', String(start));
  if (end && end > 0) params.set('end', String(end));
  if (loop) {
    params.set('loop', '1');
    params.set('playlist', vid);
  }
  return `https://www.youtube.com/embed/${vid}?${params.toString()}`;
}

